import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, NotebookPen, Trash2, Check, Share2 } from "lucide-react";
import ShareHighlightDialog from "./ShareHighlightDialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { COLORS, colorOf, readSelection, segments } from "@/lib/highlights";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const Ctx = createContext(null);
/* Search terms to <mark> inside highlightable text (e.g. arriving from /search with ?mark=). */
export const MarkTermsCtx = createContext([]);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export function markTerms(text, terms, keyBase) {
  if (!terms.length || !text) return text;
  const re = new RegExp(`(?<![a-z0-9])(${terms.map(escapeRe).join("|")})(?![a-z0-9])`, "gi");
  return text.split(re).map((part, i) =>
    terms.some((t) => t.toLowerCase() === part.toLowerCase())
      ? <mark key={`${keyBase}-m${i}`} data-search-mark className="rounded-sm bg-gold/30 px-0.5 text-foreground">{part}</mark>
      : part);
}

/* Wraps highlightable text. `scope` = {source, book, chapter, translation?, commentary?, section_id?}; `mention` builds the journal chip for a selection. */
export function HighlightScope({ scope, keys, highlights, actions, mention, children, className = "", testId }) {
  const root = useRef(null);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onUp = () => setTimeout(() => {
      const s = readSelection(root.current);
      if (s) { setSel(s); setEdit(null); }
    }, 10);
    const onDown = (e) => { if (!e.target.closest?.("[data-hl-toolbar]")) setSel(null); };
    const el = root.current;
    el?.addEventListener("mouseup", onUp);
    el?.addEventListener("touchend", onUp);
    document.addEventListener("mousedown", onDown);
    return () => { el?.removeEventListener("mouseup", onUp); el?.removeEventListener("touchend", onUp); document.removeEventListener("mousedown", onDown); };
  }, []);

  const toJournal = (text, start, end) => {
    const m = mention(start, end);
    navigate("/journal/new", { state: { seed: { quote: text, mention: m } } });
  };
  const paint = async (color) => {
    if (!user) { openAuth(); return; }
    await actions.create.mutateAsync({ ...scope, start: sel.start, end: sel.end, text: sel.text, color });
    window.getSelection()?.removeAllRanges();
    setSel(null);
  };
  const copy = async () => { await navigator.clipboard?.writeText(`"${sel.text}" — ${mention(sel.start, sel.end).label}`); toast.success("Copied."); setSel(null); };

  const openEdit = useCallback((h, rect) => { setSel(null); setEdit({ h, rect }); }, []);

  return (
    <Ctx.Provider value={{ keys, highlights, openEdit }}>
      <div ref={root} data-hl-scope className={className} data-testid={testId}>{children}</div>
      {sel && (
        <Floating rect={sel.rect} testId="selection-toolbar">
          {COLORS.map((c) => (
            <button key={c.key} type="button" onClick={() => paint(c.key)} title={`Highlight ${c.label}`} aria-label={`Highlight ${c.label}`} data-testid={`hl-color-${c.key}`}
              className="h-6 w-6 rounded-full border border-black/10 transition-transform hover:scale-125" style={{ background: c.dot }} />
          ))}
          <span className="mx-1 h-5 w-px bg-border" />
          <button type="button" onClick={() => (user ? toJournal(sel.text, sel.start, sel.end) : openAuth())} title="Add to journal" data-testid="hl-journal" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><NotebookPen className="h-4 w-4" /></button>
          <button type="button" onClick={copy} title="Copy" data-testid="hl-copy" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><Copy className="h-4 w-4" /></button>
        </Floating>
      )}
      {edit && <EditCard h={edit.h} rect={edit.rect} actions={actions} onClose={() => setEdit(null)} onJournal={() => toJournal(edit.h.text, edit.h.start, edit.h.end)} />}
    </Ctx.Provider>
  );
}

function Floating({ rect, children, testId, wide = false }) {
  const width = wide ? 320 : 290;
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
  const above = rect.top > 70;
  const style = above ? { position: "fixed", left, top: rect.top - 52, width } : { position: "fixed", left, top: rect.bottom + 10, width };
  return (
    <div data-hl-toolbar style={style} className="z-[70] flex items-center justify-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 shadow-2xl animate-in fade-in zoom-in-95" data-testid={testId} onMouseDown={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

function EditCard({ h, rect, actions, onClose, onJournal }) {
  const [label, setLabel] = useState(h.label || "");
  const [note, setNote] = useState(h.note || "");
  const [color, setColor] = useState(h.color);
  const [labels, setLabels] = useState([]);
  const [sharing, setSharing] = useState(false);
  useEffect(() => { api.get("/highlights/labels").then((r) => setLabels(r.data.labels)).catch(() => {}); }, []);
  useEffect(() => { const onKey = (e) => e.key === "Escape" && onClose(); document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey); }, [onClose]);
  const width = 340;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const top = rect.bottom + 8 + 300 > window.innerHeight ? Math.max(8, rect.top - 310) : rect.bottom + 8;
  const dirty = label !== (h.label || "") || note !== (h.note || "") || color !== h.color;
  const save = async () => { await actions.update.mutateAsync({ id: h.id, label, note, color }); onClose(); };
  return (
    <div data-hl-toolbar style={{ position: "fixed", left, top, width }} className="z-[70] space-y-3 rounded-2xl border border-border bg-card p-4 shadow-2xl animate-in fade-in zoom-in-95" data-testid="highlight-edit-card" onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {COLORS.map((c) => (
            <button key={c.key} type="button" onClick={() => setColor(c.key)} aria-label={c.label} data-testid={`hl-edit-color-${c.key}`}
              className={`flex h-6 w-6 items-center justify-center rounded-full border transition-transform hover:scale-110 ${color === c.key ? "border-foreground" : "border-black/10"}`} style={{ background: c.dot }}>
              {color === c.key && <Check className="h-3 w-3 text-black/70" />}
            </button>
          ))}
        </div>
        <button type="button" onClick={async () => { await actions.remove.mutateAsync(h.id); onClose(); }} className="rounded-md p-1.5 text-destructive hover:bg-destructive/10" aria-label="Delete highlight" data-testid="hl-delete"><Trash2 className="h-4 w-4" /></button>
      </div>
      <p className="line-clamp-3 border-l-2 pl-2 font-serif text-sm italic text-foreground/80" style={{ borderColor: colorOf(color).dot }}>“{h.text}”</p>
      <Input list="hl-labels" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Promises, Prayer, Memorize)" className="h-9 text-sm" data-testid="hl-label-input" />
      <datalist id="hl-labels">{labels.map((l) => <option key={l} value={l} />)}</datalist>
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A short note (optional)" className="text-sm" data-testid="hl-note-input" />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onJournal} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="hl-edit-journal"><NotebookPen className="h-3.5 w-3.5" /> Journal</button>
          <button type="button" onClick={() => setSharing(true)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="hl-edit-share"><Share2 className="h-3.5 w-3.5" /> Share card</button>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={onClose} data-testid="hl-edit-cancel">Close</Button>
          <Button type="button" size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={save} disabled={!dirty || actions.update.isPending} data-testid="hl-edit-save">Save</Button>
        </div>
      </div>
      {sharing && <ShareHighlightDialog h={h} onClose={() => setSharing(false)} />}
    </div>
  );
}

/* One keyed text container with painted highlights. Must be used inside HighlightScope. */
export function HighlightText({ k, text, className = "" }) {
  const ctx = useContext(Ctx);
  const terms = useContext(MarkTermsCtx);
  const segs = ctx ? segments(text, k, ctx.keys, ctx.highlights) : [{ text, h: null }];
  return (
    <span data-hl-key={k} className={className}>
      {segs.map((s, i) => s.h ? (
        <mark key={`h-${s.h.id}-${i}`} data-hl-id={s.h.id} data-testid={`hl-mark-${s.h.id}`} title={s.h.label ? `${s.h.label}${s.h.note ? " · " + s.h.note : ""}` : s.h.note || "Click to label or note"}
          onClick={(e) => { e.stopPropagation(); ctx.openEdit(s.h, e.currentTarget.getBoundingClientRect()); }}
          className="hl-mark cursor-pointer rounded-sm text-inherit transition-colors" style={{ background: colorOf(s.h.color).bg }}>{s.text}</mark>
      ) : <span key={`t-${i}-${s.text.length}`}>{markTerms(s.text, terms, `${k}-${i}`)}</span>)}
    </span>
  );
}

/* Scroll to and flash a highlight when ?hl=id is present. */
export function useFlashHighlight(hl, ready) {
  useEffect(() => {
    if (!hl || !ready) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-hl-id="${hl}"]`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("hl-flash");
      setTimeout(() => el.classList.remove("hl-flash"), 2500);
    }, 400);
    return () => clearTimeout(t);
  }, [hl, ready]);
}
